import { type Ref, ref, watch } from "vue";

function readInitial(storageKey: string, fallback: number[]): number[] {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((value) => typeof value === "number")) return parsed;
  } catch {
    // Corrupt or unavailable storage — fall back to the default selection.
  }
  return fallback;
}

/**
 * A reactive `Set<number>` mirrored to localStorage, so a selection survives
 * navigation and reloads. Hydrated from the stored value (or `fallback` when
 * absent/unreadable) and rewritten on every change. Pass a distinct
 * `storageKey` per feature to keep independent selections from colliding.
 */
export function usePersistedSet(storageKey: string, fallback: number[] = []): Ref<Set<number>> {
  const state = ref(new Set<number>(readInitial(storageKey, fallback))) as Ref<Set<number>>;

  watch(
    state,
    (value) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(Array.from(value)));
      } catch {
        // Ignore quota / serialization / unavailable-storage errors.
      }
    },
    { deep: true },
  );

  return state;
}
